import {
  Button,
  Chip,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ImageNotSupportedRoundedIcon from "@mui/icons-material/ImageNotSupportedRounded";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Shared design tokens for this file. Duplicated per-file on purpose so each
// component stays drop-in-able without depending on a shared theme file.
const tokens = {
  ink: "#12242E",
  teal: "#0F8B8D",
  tealDark: "#0A5F60",
  line: "rgba(18,36,46,0.10)",
  headingFont: "'Sora', sans-serif",
};

// Shared keyframes, applied via sx={{ ...fadeInUp, animation: "fadeInUp .5s ease both" }}
const fadeInUp = {
  "@keyframes fadeInUp": {
    from: { opacity: 0, transform: "translateY(16px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
};
const fadeIn = {
  "@keyframes fadeIn": {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
};

// Validates/fixes a (possibly malformed) image URL, or returns null if it
// can't be turned into something usable.
const getSafeImageUrl = (rawUrl) => {
  if (!rawUrl || typeof rawUrl !== "string") return null;
  let url = rawUrl.trim();
  if (!url) return null;

  if (url.startsWith("//")) {
    url = "https:" + url;
  } else if (!/^https?:\/\//i.test(url)) {
    url = "https://" + url.replace(/^\/+/, "");
  }

  const match = url.match(/^(https?:\/\/)(.*)$/i);
  if (!match) return null;
  const [, protocol, rest] = match;
  const finalUrl = protocol + rest.replace(/\/{2,}/g, "/");

  try {
    // eslint-disable-next-line no-new
    new URL(finalUrl);
    return finalUrl;
  } catch (e) {
    return null;
  }
};

const ExpertStories = ({ id }) => {
  const navigate = useNavigate();
  const [showData, setShowData] = useState("");
  const [found, setFound] = useState({});
  const [listInfoData, setListInfoData] = useState([]);

  useEffect(() => {
    setListInfoData([]);
    setFound({});
    setShowData("");
    fetch(
      `https://aps.songjukti.com/profile/login/api_webland/cat_listInfo.php?eid=${id}`
    )
      .then((res) => res.json())
      .then((result) => {
        setListInfoData(result || []);
        setFound(result?.[0] || {});
      })
      .catch((error) => console.log("listInfoData Error => ", error));
  }, [id]);

  useEffect(() => {
    if (!showData) return;
    const match = listInfoData.find((element) => element.refer === showData);
    if (match) setFound(match);
  }, [showData, listInfoData]);

  const isLoading = listInfoData.length === 0;
  const bannerUrl = getSafeImageUrl(found?.banner_pic);

  const goToExperts = () => navigate(`/expertlist/${id}`);

  return (
    <Box sx={{ pb: { xs: 5, md: 7 } }}>
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          borderRadius: "20px",
          border: `1px solid ${tokens.line}`,
          overflow: "hidden",
          bgcolor: "white",
          ...fadeInUp,
          animation: "fadeInUp 0.55s ease both",
        }}
      >
        <Grid container>
          {/* Nav: labeled by subcat when the API provides one, falling back
              to the category's type (subcat can come back as an empty
              string, as it does for e.g. "DTP"). The pill list scrolls
              internally instead of growing past the column's height — if it
              just overflowed, the parent Paper's overflow:hidden (needed for
              the rounded corners) would clip the "Browse service" button
              right out of view whenever a category has a lot of subcats. */}
          <Grid
            item
            xs={12}
            md={3.5}
            sx={{
              borderRight: { md: `1px solid ${tokens.line}` },
              borderBottom: { xs: `1px solid ${tokens.line}`, md: "none" },
              bgcolor: `${tokens.teal}08`,
            }}
          >
            <Stack
              justifyContent="space-between"
              sx={{ p: { xs: 2, md: 3 }, height: "100%" }}
            >
              <Box
                sx={{
                  flex: { md: 1 },
                  minHeight: 0,
                  overflowY: { md: "auto" },
                  overflowX: { xs: "auto", md: "visible" },
                }}
              >
                <Stack
                  direction={{ xs: "row", md: "column" }}
                  spacing={{ xs: 1, md: 0.5 }}
                  sx={{ pb: { xs: 1, md: 0 } }}
                >
                  {isLoading
                    ? Array.from({ length: 4 }).map((_, i) => (
                        <Skeleton
                          key={i}
                          variant="rounded"
                          width={140}
                          height={38}
                          sx={{ borderRadius: "10px", flexShrink: 0 }}
                        />
                      ))
                    : listInfoData.map((element) => {
                        const active = element.refer === found?.refer;
                        const label = element?.subcat || element?.type || "—";
                        return (
                          <Box
                            key={element.refer}
                            onClick={() => setShowData(element.refer)}
                            sx={{
                              flexShrink: 0,
                              px: 2,
                              py: 1.2,
                              borderRadius: "10px",
                              cursor: "pointer",
                              bgcolor: active
                                ? `${tokens.teal}14`
                                : "transparent",
                              borderLeft: {
                                md: `3px solid ${
                                  active ? tokens.teal : "transparent"
                                }`,
                              },
                              transition:
                                "background-color 0.2s ease, transform 0.15s ease",
                              "&:hover": {
                                bgcolor: active
                                  ? `${tokens.teal}1f`
                                  : "rgba(18,36,46,0.04)",
                                transform: "translateX(2px)",
                              },
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "11.5pt",
                                fontWeight: active ? 700 : 500,
                                color: active ? tokens.tealDark : tokens.ink,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {label}
                            </Typography>
                          </Box>
                        );
                      })}
                </Stack>
              </Box>

              <Button
                variant="contained"
                endIcon={<ArrowForwardRoundedIcon />}
                onClick={() => navigate("/servicelist/" + id)}
                sx={{
                  mt: { xs: 2, md: 3 },
                  flexShrink: 0,
                  alignSelf: "flex-start",
                  bgcolor: tokens.teal,
                  borderRadius: "30px",
                  textTransform: "capitalize",
                  fontWeight: 600,
                  px: 2.5,
                  transition: "background-color 0.2s ease, transform 0.15s ease",
                  "&:hover": { bgcolor: tokens.tealDark, transform: "translateY(-2px)" },
                }}
              >
                Browse service
              </Button>
            </Stack>
          </Grid>

          {/* Banner hero: title + type chip overlaid on the image. Clicking
              the banner takes you to the expert list for this category. The
              long "info" description renders below the panel, not on the
              photo, since it's usually a full paragraph. */}
          <Grid item xs={12} md={8.5}>
            <Box
              onClick={bannerUrl || !isLoading ? goToExperts : undefined}
              sx={{
                position: "relative",
                height: { xs: "32vh", md: "48vh" },
                cursor: isLoading ? "default" : "pointer",
              }}
            >
              {isLoading ? (
                <Skeleton variant="rectangular" width="100%" height="100%" />
              ) : bannerUrl ? (
                <Box
                  key={found?.refer}
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    ...fadeIn,
                    animation: "fadeIn 0.4s ease both",
                  }}
                >
                  <Box
                    component="img"
                    alt={found?.title || "Service"}
                    src={bannerUrl}
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background:
                        "linear-gradient(0deg, rgba(15,42,56,0.80) 0%, rgba(15,42,56,0.08) 55%)",
                    }}
                  />
                  <Stack
                    spacing={0.75}
                    sx={{
                      position: "absolute",
                      left: { xs: 20, md: 32 },
                      bottom: { xs: 18, md: 26 },
                      right: 20,
                    }}
                  >
                    {found?.type && (
                      <Chip
                        label={found.type}
                        size="small"
                        sx={{
                          alignSelf: "flex-start",
                          bgcolor: tokens.teal,
                          color: "white",
                          fontWeight: 600,
                        }}
                      />
                    )}
                    <Typography
                      sx={{
                        fontFamily: tokens.headingFont,
                        color: "white",
                        fontWeight: 700,
                        fontSize: { xs: "16pt", md: "23pt" },
                      }}
                    >
                      {found?.title}
                    </Typography>
                  </Stack>
                </Box>
              ) : (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  spacing={1}
                  sx={{ width: "100%", height: "100%", bgcolor: "#F0EFEB" }}
                >
                  <ImageNotSupportedRoundedIcon
                    sx={{ fontSize: "28pt", color: "rgba(18,36,46,0.35)" }}
                  />
                  <Typography
                    sx={{
                      fontSize: "10pt",
                      fontWeight: 600,
                      color: "rgba(18,36,46,0.5)",
                    }}
                  >
                    No image available
                  </Typography>
                </Stack>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {found?.info && (
        <Typography
          key={found?.refer}
          sx={{
            mt: 2,
            fontSize: { xs: "10pt", md: "11.5pt" },
            color: "rgba(18,36,46,0.6)",
            maxWidth: 720,
            ...fadeInUp,
            animation: "fadeInUp 0.5s ease 0.1s both",
          }}
        >
          {found.info}
        </Typography>
      )}
    </Box>
  );
};

export default ExpertStories;